import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Invoice Amendment modal — opened from PmsInvoiceRegister's "Amend" row action.
// SRS_INVOICE_AMENDMENT.md §10.2.
//
// Three tabs:
//   Tab 1 — Narrative edit (Bill-To, GSTIN, remarks; tax math NOT touched)
//   Tab 2 — Cancel & Reissue (issues a CN; reopens folio for re-billing)
//   Tab 3 — Add Charges after check-out (SRS_POST_CHECKOUT_CHARGES.md):
//           replacement invoice (inside the amendment window) or a
//           supplementary invoice (any time).
//
// Props:
//   token, invoice, selectedProperty, onClose, onAmended (callback fired after success)

// Blocks that apply to every tab, including Add Charges. WINDOW_EXPIRED is
// not here: it only rules out reissuing — a supplementary invoice is allowed.
const HARD_BLOCKS = new Set(['PERMISSION_DENIED', 'STATUS_NOT_FINAL', 'POLICY_DISABLED']);

export default function InvoiceAmendModal({ token, invoice, selectedProperty, onClose, onAmended }) {
  const { isMobile } = useIsMobile();
  const [can, setCan] = useState(null);  // { allowed, narrative_allowed, ... }
  const [tab, setTab] = useState('narrative');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tab 1 — narrative form state
  const [form, setForm] = useState({
    guest_name: invoice?.guest_name || '',
    guest_mobile: invoice?.guest_mobile || '',
    guest_email: invoice?.guest_email || '',
    customer_gstin: invoice?.customer_gstin || '',
    place_of_supply: invoice?.place_of_supply || '',
    customer_state_code: invoice?.customer_state_code || '',
    reason: ''
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  // Tab 2 — Cancel & Reissue state
  const [cnReason, setCnReason] = useState('');
  const [cnAck, setCnAck] = useState(false);
  const [cnBusy, setCnBusy] = useState(false);
  const [cnResult, setCnResult] = useState(null);  // { credit_note_no, ... }

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/v1/pms/invoices/${invoice.invoice_id}/can-amend`, { headers: headers() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to evaluate amendment policy');
        setCan(data);
        // Outside the window only Add Charges (supplementary) can still act.
        if (!data.allowed && !HARD_BLOCKS.has(data.blocked_reason)) setTab('charges');
      } catch (e) {
        setError(e.message || 'Failed to evaluate amendment policy');
      } finally { setLoading(false); }
    })();
  }, [invoice.invoice_id, headers]);

  const blockedReasonHuman = (code) => ({
    PERMISSION_DENIED:   'You do not have permission to amend invoices.',
    STATUS_NOT_FINAL:    'This invoice is not in FINAL status (it may already be CANCELLED).',
    POLICY_DISABLED:     'Invoice amendment is disabled for this property. Enable it in Hotel Config → PMS Policy.',
    WINDOW_EXPIRED:      'The amendment window for this invoice has expired. Adjust the policy in Hotel Config → PMS Policy.',
    NOT_IMPLEMENTED:     'This action is not yet implemented (coming in a later phase).'
  }[code] || `Cannot amend: ${code || 'unknown reason'}`);

  const submitCancelAndReissue = async () => {
    if (!cnReason.trim()) { setError('Reason is required'); return; }
    if (!cnAck) { setError('Please tick the acknowledgement checkbox'); return; }
    setCnBusy(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`/api/v1/pms/invoices/${invoice.invoice_id}/cancel-and-reissue`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ reason: cnReason.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Cancel & Reissue failed');
      setCnResult(data);
      setSuccess(`Invoice ${data.original_invoice_no} cancelled. Credit Note ${data.credit_note_no} issued.`);
      if (typeof onAmended === 'function') onAmended(null);
    } catch (e) {
      setError(e.message || 'Cancel & Reissue failed');
    } finally { setCnBusy(false); }
  };

  const submitNarrative = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      // Compose only changed fields. Server validates again.
      const body = { reason: form.reason || null };
      const trimmedName = (form.guest_name || '').trim();
      const trimmedGstin = (form.customer_gstin || '').trim().toUpperCase();
      if (trimmedName && trimmedName !== (invoice.guest_name || '')) body.guest_name = trimmedName;
      if (trimmedGstin !== (invoice.customer_gstin || '')) body.customer_gstin = trimmedGstin || null;

      // Contact fields — these print on the invoice and are the WhatsApp / Email
      // share targets, so a dummy number keyed at check-in has to be fixable here.
      const trimmedMobile = (form.guest_mobile || '').trim();
      const trimmedEmail = (form.guest_email || '').trim().toLowerCase();
      if (trimmedMobile !== (invoice.guest_mobile || '')) body.guest_mobile = trimmedMobile || null;
      if (trimmedEmail !== (invoice.guest_email || '')) body.guest_email = trimmedEmail || null;

      // Detect place_of_supply / state_code changes — server will reject with
      // RETRY_VIA_REISSUE, but warn the user up front.
      if (form.place_of_supply !== (invoice.place_of_supply || '')) body.place_of_supply = form.place_of_supply || null;
      if (form.customer_state_code !== (invoice.customer_state_code || '')) body.customer_state_code = form.customer_state_code || null;

      const res = await fetch(`/api/v1/pms/invoices/${invoice.invoice_id}/narrative`, {
        method: 'PATCH', headers: headers(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.status === 422 && data.code === 'RETRY_VIA_REISSUE') {
        setError('Changing the State Code or Place of Supply could change the CGST/SGST <-> IGST split. Use Cancel & Reissue (Tab 2) to recompute correctly. Phase 2 of this feature ships Cancel & Reissue.');
        setSaving(false);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Save failed');
      if (data.no_change) {
        setSuccess('No fields changed.');
      } else {
        setSuccess('Invoice amended successfully.');
      }
      if (typeof onAmended === 'function') onAmended(data.invoice);
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={isMobile ? { ...overlay, padding: 8 } : overlay}>
      <div style={isMobile ? { ...dialog, padding: 14 } : dialog}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: '#1e293b' }}>Amend Invoice</h3>
          <button onClick={onClose} style={closeX}>×</button>
        </div>

        <div style={{ background: '#f8fafc', borderRadius: 6, padding: 10, marginBottom: 12, fontSize: '0.875rem' }}>
          <div><b>Invoice:</b> {invoice.invoice_no} · <b>Room:</b> {invoice.room_number || '—'} · <b>Guest:</b> {invoice.guest_name || '—'}</div>
          <div style={{ color: '#64748b', fontSize: '0.78rem' }}>
            Balance: ₹{Number(invoice.balance || 0).toFixed(2)} · Settlement: {invoice.settlement_mode || '—'}
          </div>
        </div>

        {/* Tab strip */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 12 }}>
          <button onClick={() => setTab('narrative')} style={tabBtn(tab === 'narrative')}>
            Narrative Edit
          </button>
          <button onClick={() => setTab('reissue')} style={tabBtn(tab === 'reissue')}>
            Cancel &amp; Reissue
            {can && !can.cancel_reissue_allowed && <span style={{ fontSize: '0.7rem', marginLeft: 6, color: '#94a3b8' }}>(coming soon)</span>}
          </button>
          <button onClick={() => setTab('charges')} style={tabBtn(tab === 'charges')}>
            Add Charges
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading amendment policy…</div>
        ) : tab === 'charges' && can && (can.allowed || !HARD_BLOCKS.has(can.blocked_reason)) ? (
          <AddChargesTab
            invoice={invoice}
            reissueAllowed={Boolean(can.allowed)}
            headers={headers}
            onClose={onClose}
            onAmended={onAmended}
          />
        ) : !can?.allowed ? (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: 12, borderRadius: 6, border: '1px solid #fcd34d' }}>
            {blockedReasonHuman(can?.blocked_reason)}
            {can?.window_policy && (
              <div style={{ fontSize: '0.78rem', marginTop: 6, color: '#78350f' }}>
                Current policy: <code>{can.window_policy}</code>
                {can.window_days_remaining != null && `, ${can.window_days_remaining} day(s) remaining`}
              </div>
            )}
            <div style={{ marginTop: 10, textAlign: 'right' }}>
              <button onClick={onClose} style={btnSecondary}>Close</button>
            </div>
          </div>
        ) : tab === 'narrative' ? (
          <>
            <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#475569' }}>
              Safe edits — these change <b>display fields only</b> (Bill-To name, contact, GSTIN, address).
              Tax amounts and lines are not affected. The original invoice number is preserved.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '150px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={lbl}>Bill-To name</label>
              <input style={inp} value={form.guest_name}
                onChange={(e) => setForm({ ...form, guest_name: e.target.value })} maxLength={200} />

              <label style={lbl}>Mobile</label>
              <input style={inp} value={form.guest_mobile}
                onChange={(e) => setForm({ ...form, guest_mobile: e.target.value })}
                maxLength={30} placeholder="Guest mobile (WhatsApp target)" />

              <label style={lbl}>Email</label>
              <input style={inp} value={form.guest_email}
                onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                maxLength={120} placeholder="Guest email (invoice mail target)" />

              <label style={lbl}>Customer GSTIN</label>
              <input style={inp} value={form.customer_gstin}
                onChange={(e) => setForm({ ...form, customer_gstin: e.target.value.toUpperCase() })}
                maxLength={15} placeholder="29ABCDE1234F1Z5" />

              <label style={lbl}>State code (auto)</label>
              <input style={{ ...inp, background: '#f1f5f9' }}
                value={form.customer_state_code}
                onChange={(e) => setForm({ ...form, customer_state_code: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                placeholder="(derived from GSTIN)" />

              <label style={lbl}>Place of supply</label>
              <input style={inp} value={form.place_of_supply}
                onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} maxLength={40} />

              <label style={lbl}>Reason for amendment</label>
              <input style={inp} value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Guest emailed correction to company name" />
            </div>

            {(form.place_of_supply !== (invoice.place_of_supply || '')
              || form.customer_state_code !== (invoice.customer_state_code || '')) && (
              <div style={{ marginTop: 10, background: '#fef3c7', color: '#92400e', padding: 8, borderRadius: 6, fontSize: '0.78rem', border: '1px solid #fcd34d' }}>
                ⚠ Changing Place of Supply or State code may flip the GST split between CGST/SGST and IGST. If it does, use Cancel &amp; Reissue (Tab 2) to recompute the tax — the server will reject this Save if the flip is detected.
              </div>
            )}

            {error && <div style={errBox}>{error}</div>}
            {success && <div style={okBox}>{success}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button onClick={submitNarrative} disabled={saving} style={btnPrimary}>
                {saving ? 'Saving…' : 'Save Narrative'}
              </button>
            </div>
          </>
        ) : cnResult ? (
          // Tab 2 — success state after Cancel & Reissue
          <div>
            <div style={{ background: '#dcfce7', color: '#166534', padding: 14, borderRadius: 6, border: '1px solid #86efac', fontSize: '0.9rem' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                ✓ Invoice cancelled successfully.
              </div>
              <div>
                <b>Original:</b> {cnResult.original_invoice_no} (now CANCELLED)<br />
                <b>Credit Note:</b> {cnResult.credit_note_no}<br />
                <b>Ledger rows reversed:</b> {cnResult.ledger_rows_reversed}<br />
                {Number(cnResult.carry_forward_credit || 0) > 0 && (
                  <>
                    <b>Carried-forward payment:</b> ₹{Number(cnResult.carry_forward_credit).toFixed(2)}<br />
                  </>
                )}
              </div>
            </div>
            <div style={{ marginTop: 12, background: '#dbeafe', color: '#1e40af', padding: 12, borderRadius: 6, border: '1px solid #93c5fd', fontSize: '0.85rem' }}>
              <b>Next:</b> Open <b>Front Office → Command Center</b>, find stay #{cnResult.stay_id || invoice.stay_id || '—'},
              open its Folio editor to add/remove corrected charges, and finalise the new invoice via the
              normal checkout-invoice flow. The carried-forward payment is already on the folio.
            </div>
            <div style={{ marginTop: 12, background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 6, border: '1px solid #86efac', fontSize: '0.8rem' }}>
              ✓ Credit Note <b>{cnResult.credit_note_no}</b> will be pushed to Tally as a
              "Credit Note" voucher on the next Tally Live Push batch (or run the push
              manually from <b>Accounting → Tally Live Push</b>).
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </div>
        ) : (
          // Tab 2 — Cancel & Reissue confirmation form
          <div>
            <div style={{ background: '#fef3c7', color: '#92400e', padding: 12, borderRadius: 6, border: '1px solid #fcd34d', fontSize: '0.875rem', marginBottom: 12 }}>
              <b>This action is irreversible.</b> The current invoice will be marked CANCELLED,
              a Credit Note will be issued, and the folio will be reopened for re-billing.
              The customer's payment of <b>₹{Number(invoice.total_credit || 0).toFixed(2)}</b> carries forward
              to the new invoice automatically.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '150px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={lbl}>Cancellation reason</label>
              <textarea
                value={cnReason}
                onChange={(e) => setCnReason(e.target.value)}
                rows={3}
                placeholder="e.g. Guest disputed extra-bed charge; corrected line removed."
                style={{ ...inp, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={cnAck} onChange={(e) => setCnAck(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                I confirm the customer has agreed to this correction and the audit trail is acceptable.
                A Credit Note will be generated against the original invoice for GST-compliance.
              </span>
            </label>

            {error && <div style={errBox}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button onClick={submitCancelAndReissue} disabled={cnBusy || !cnReason.trim() || !cnAck}
                style={{ ...btnPrimary, background: cnBusy || !cnReason.trim() || !cnAck ? '#94a3b8' : '#dc2626' }}>
                {cnBusy ? 'Processing…' : 'Cancel & Reissue Invoice'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 3 — Add Charges after check-out ───────────────────────────────────
// Server: POST /invoices/:id/add-charges (docs/SRS_POST_CHECKOUT_CHARGES.md).
// Services come from Front Office → Masters → Service Master
// (GET /service-master); payment / waiver types aren't charges.
const NON_CHARGE_TYPES = new Set(['ADVANCE', 'ADVANCE_PAYMENT', 'PAYMENT', 'CHECKOUT_PAYMENT', 'REFUND', 'ALLOWANCE', 'VOID']);
const GST_RATES = [0, 5, 12, 18, 28];
const PAY_MODES = ['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'CHEQUE'];
const money = (v) => Number(Number(v || 0).toFixed(2));
const fmt = (v) => money(v).toFixed(2);
const sixDigitSac = (v) => (/^\d{6}$/.test(String(v || '').trim()) ? String(v).trim() : '');
const blankLine = () => ({ service_id: '', description: '', sac_code: '', amount: '', inclusive: true, gst_percent: 5 });

// Same rounding as the server (SRS §5) so the preview matches what posts.
function priceLine(line, isInter) {
  const amount = money(line.amount);
  const g = Number(line.gst_percent || 0);
  const taxable = line.inclusive ? money(amount / (1 + g / 100)) : amount;
  const tax = line.inclusive ? money(amount - taxable) : money(taxable * g / 100);
  const cgst = isInter ? 0 : money(tax / 2);
  return { taxable, tax, cgst, sgst: isInter ? 0 : money(tax - cgst), igst: isInter ? tax : 0, total: money(taxable + tax) };
}

function AddChargesTab({ invoice, reissueAllowed, headers, onClose, onAmended }) {
  const { isMobile } = useIsMobile();
  const mode = String(invoice.settlement_mode || '').toUpperCase();
  const partyName = mode === 'CORPORATE' && invoice.settled_corporate_id
    ? (invoice.settled_corporate_name || 'the company')
    : mode === 'TRAVEL_AGENT' && invoice.settled_travel_agent_id
      ? (invoice.settled_agent_name || 'the travel agent')
      : null;
  const isInter = String(invoice.supply_type || 'INTRA').toUpperCase() === 'INTER';

  const [lines, setLines] = useState([blankLine()]);
  const [method, setMethod] = useState(reissueAllowed ? 'REISSUE' : 'SUPPLEMENTARY');
  const [settle, setSettle] = useState(partyName ? 'BILL_TO_PARTY' : 'DUE');
  const [payMode, setPayMode] = useState('CASH');
  const [payRef, setPayRef] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/v1/pms/service-master', { headers: headers() });
        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error(data?.error || 'Could not load the Service Master');
        setServices((Array.isArray(data) ? data : [])
          .filter((s) => !NON_CHARGE_TYPES.has(String(s.posting_type || '').toUpperCase())));
      } catch (e) {
        setErr(e.message || 'Could not load the Service Master');
      } finally { setServicesLoading(false); }
    })();
  }, [headers]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  // Picking a service fills the description (unless the user typed their
  // own) and the SAC from the master.
  const pickService = (i, serviceId) => {
    const l = lines[i];
    const prev = services.find((s) => String(s.service_id) === String(l.service_id));
    const next = services.find((s) => String(s.service_id) === String(serviceId));
    const keepDesc = l.description.trim() && l.description !== (prev?.service_name || '');
    setLine(i, {
      service_id: serviceId,
      description: keepDesc ? l.description : (next?.service_name || ''),
      sac_code: sixDigitSac(next?.sac_code) || l.sac_code
    });
  };
  const priced = lines.map((l) => priceLine(l, isInter));
  const extra = money(priced.reduce((s, p) => s + p.total, 0));
  const validLines = lines.every((l) => l.service_id && Number(l.amount) > 0);

  const submit = async () => {
    if (!validLines) { setErr('Choose a service and enter an amount greater than 0 on every line.'); return; }
    if (!reason.trim()) { setErr('Reason is required.'); return; }
    if (settle === 'COLLECT_NOW' && (payMode === 'CARD' || payMode === 'UPI') && !payRef.trim()) {
      setErr(`Reference no. is required for ${payMode}.`); return;
    }
    setBusy(true); setErr('');
    try {
      const res = await fetch(`/api/v1/pms/invoices/${invoice.invoice_id}/add-charges`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          method,
          reason: reason.trim(),
          charges: lines.map((l) => ({
            service_id: Number(l.service_id), description: l.description.trim(), sac_code: l.sac_code.trim(),
            amount: money(l.amount), amount_includes_gst: l.inclusive, gst_percent: Number(l.gst_percent)
          })),
          settlement: settle === 'COLLECT_NOW'
            ? { type: settle, payment_mode: payMode, reference_no: payRef.trim() }
            : { type: settle }
        })
      });
      // A non-JSON reply (an HTML "Cannot POST" page) means the server
      // doesn't have this endpoint — say so instead of a JSON parse error.
      const raw = await res.text();
      let data;
      try { data = JSON.parse(raw); } catch (_) {
        throw new Error(res.status === 404
          ? 'The server does not support Add Charges yet (HTTP 404). The backend needs to be updated — please contact support.'
          : `Unexpected server response (HTTP ${res.status}). Please try again or contact support.`);
      }
      if (!res.ok) throw new Error(data.error || 'Adding charges failed');
      setResult(data);
      if (typeof onAmended === 'function') onAmended(null);
    } catch (e) {
      setErr(e.message || 'Adding charges failed');
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div>
        <div style={{ background: '#dcfce7', color: '#166534', padding: 14, borderRadius: 6, border: '1px solid #86efac', fontSize: '0.9rem' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>✓ {result.message}</div>
          <div>
            <b>{result.method === 'REISSUE' ? 'Replacement invoice' : 'Supplementary invoice'}:</b> {result.new_invoice_no}<br />
            {result.credit_note_no && <><b>Credit Note:</b> {result.credit_note_no} (for {result.original_invoice_no})<br /></>}
            <b>Charges added:</b> ₹{fmt(result.extra_total)}<br />
            <b>Invoice total:</b> ₹{fmt(result.totals?.total_debit)} · Taxable ₹{fmt(result.totals?.taxable)} · GST ₹{fmt(result.totals?.tax)}<br />
            <b>Net payable:</b> ₹{fmt(result.totals?.balance)}
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: '0.8rem', color: '#475569' }}>
          Share the new invoice with the customer from the Invoice Register.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={btnPrimary}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#475569' }}>
        Bill charges that came in after check-out. The issued invoice is never edited in place.
      </p>

      {lines.map((l, i) => (
        <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 8 }}>
            <select style={inp} value={l.service_id} onChange={(e) => pickService(i, e.target.value)}>
              <option value="">{servicesLoading ? 'Loading services…' : (services.length ? 'Select service' : 'No services in Service Master')}</option>
              {services.map((s) => <option key={s.service_id} value={s.service_id}>{s.service_name}</option>)}
            </select>
            <input style={inp} value={l.description} maxLength={255}
              onChange={(e) => setLine(i, { description: e.target.value })}
              placeholder="Description, e.g. Airport Drop Charges" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.3fr) minmax(0,0.8fr)', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <input style={inp} type="number" min="0.01" step="0.01" value={l.amount}
              onChange={(e) => setLine(i, { amount: e.target.value })} placeholder="Amount ₹" />
            <select style={inp} value={l.gst_percent} onChange={(e) => setLine(i, { gst_percent: Number(e.target.value) })}>
              {GST_RATES.map((g) => <option key={g} value={g}>GST {g}%</option>)}
            </select>
            <select style={inp} value={l.inclusive ? 'INC' : 'EXC'} onChange={(e) => setLine(i, { inclusive: e.target.value === 'INC' })}>
              <option value="INC">Inclusive of GST</option>
              <option value="EXC">Exclusive of GST</option>
            </select>
            <input style={inp} value={l.sac_code} maxLength={6} title="SAC code"
              onChange={(e) => setLine(i, { sac_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} placeholder="SAC" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: '0.78rem', color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
            <span>
              Taxable ₹{fmt(priced[i].taxable)} · {isInter ? `IGST ₹${fmt(priced[i].igst)}` : `CGST ₹${fmt(priced[i].cgst)} · SGST ₹${fmt(priced[i].sgst)}`} · <b>Amount ₹{fmt(priced[i].total)}</b>
            </span>
            {lines.length > 1 && (
              <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.78rem' }}>Remove</button>
            )}
          </div>
        </div>
      ))}
      <button onClick={() => setLines((ls) => [...ls, blankLine()])}
        style={{ ...btnSecondary, padding: '5px 12px', fontSize: '0.8rem' }}>+ Add another charge</button>

      <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
        <div style={lbl}>How to bill it</div>
        <label style={radioRow(method === 'REISSUE', !reissueAllowed)}>
          <input type="radio" name="addchg-method" checked={method === 'REISSUE'} disabled={!reissueAllowed}
            onChange={() => setMethod('REISSUE')} />
          <span>
            <b>Replace the invoice</b> — one invoice for ₹{fmt(Number(invoice.total_debit || 0) + extra)}.
            {' '}{invoice.invoice_no} is cancelled with a credit note.
            {!reissueAllowed && <span style={{ color: '#b45309' }}> Not available: the amendment window has closed.</span>}
          </span>
        </label>
        <label style={radioRow(method === 'SUPPLEMENTARY', false)}>
          <input type="radio" name="addchg-method" checked={method === 'SUPPLEMENTARY'} onChange={() => setMethod('SUPPLEMENTARY')} />
          <span><b>Supplementary invoice</b> — a separate invoice for ₹{fmt(extra)}. {invoice.invoice_no} stays as issued.</span>
        </label>
      </div>

      <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '150px 1fr', gap: 8, alignItems: 'center' }}>
        <label style={lbl}>Settle ₹{fmt(extra)}</label>
        <select style={inp} value={settle} onChange={(e) => setSettle(e.target.value)}>
          {partyName && <option value="BILL_TO_PARTY">Bill to {partyName}</option>}
          <option value="COLLECT_NOW">Collect now</option>
          <option value="DUE">Leave due (collect later)</option>
        </select>
        {settle === 'COLLECT_NOW' && (
          <>
            <label style={lbl}>Payment</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: 8 }}>
              <select style={inp} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                {PAY_MODES.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
              <input style={inp} value={payRef} onChange={(e) => setPayRef(e.target.value)} maxLength={100}
                placeholder={payMode === 'CARD' || payMode === 'UPI' ? 'Reference no. (required)' : 'Reference no. (optional)'} />
            </div>
          </>
        )}
        <label style={lbl}>Reason</label>
        <input style={inp} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
          placeholder="e.g. Airport drop requested by guest, confirmed by company" />
      </div>

      {err && <div style={errBox}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy || !validLines || !reason.trim()}
          style={{ ...btnPrimary, background: busy || !validLines || !reason.trim() ? '#94a3b8' : '#2563eb' }}>
          {busy ? 'Saving…' : (method === 'REISSUE' ? `Replace invoice (+₹${fmt(extra)})` : `Issue supplementary invoice (₹${fmt(extra)})`)}
        </button>
      </div>
    </div>
  );
}

const radioRow = (active, disabled) => ({
  display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 6, fontSize: '0.84rem',
  border: active ? '1px solid #2563eb' : '1px solid #e2e8f0', background: active ? '#eff6ff' : '#fff',
  color: disabled ? '#94a3b8' : '#334155', cursor: disabled ? 'not-allowed' : 'pointer'
});

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.55)', zIndex: 1500,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
};
const dialog = {
  background: '#fff', borderRadius: 10, padding: 22,
  maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 24px 60px rgba(15,23,42,0.25)'
};
const closeX = { background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer' };
const tabBtn = (active) => ({
  background: 'none', border: 'none', padding: '8px 16px',
  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
  color: active ? '#1e40af' : '#64748b',
  fontWeight: active ? 600 : 500, fontSize: '0.9rem', cursor: 'pointer'
});
const lbl = { color: '#475569', fontSize: '0.85rem', fontWeight: 500 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const errBox = { marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: '0.85rem' };
const okBox  = { marginTop: 12, background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 6, fontSize: '0.85rem' };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
